import { useEffect, useMemo, useState } from "react";
import PageShell from "../components/layout/PageShell";
import { getLeadKanban, updateLeadStatus } from "../services/crmService";
import toast from "react-hot-toast";

import "./leads.css";

const STATUS_ORDER = ["New", "Contacted", "Won", "Lost"];

const nextStatusMap = {
    New: "Contacted",
    Contacted: "Won",
    Won: "Lost",
    Lost: "New"
};

function formatDate(value) {
    if (!value) return "-";

    try {
        return new Date(value).toLocaleString();
    } catch {
        return "-";
    }
}

function getStatusHelp(status) {
    if (status === "New") return "New website enquiries waiting for first follow-up.";
    if (status === "Contacted") return "Leads your team has already reached out to.";
    if (status === "Won") return "Converted or successfully closed leads.";
    if (status === "Lost") return "Closed leads that did not convert.";

    return "Lead pipeline status.";
}

function normalizeStatus(value) {
    return String(value || "New").trim() || "New";
}

function orderGroups(groups = []) {
    const map = new Map();

    groups.forEach((group) => {
        map.set(normalizeStatus(group.status), {
            ...group,
            status: normalizeStatus(group.status),
            leads: Array.isArray(group.leads) ? group.leads : []
        });
    });

    return STATUS_ORDER.map((status) => {
        return map.get(status) || {
            status,
            leads: []
        };
    });
}

function getLeadName(lead) {
    return lead?.fullName || lead?.name || "Unnamed Lead";
}

function getLeadInterest(lead) {
    return (
        lead?.programName ||
        lead?.itemTitle ||
        lead?.ctaLabel ||
        lead?.message ||
        "Website enquiry"
    );
}

function KanbanStat({ label, value }) {
    return (
        <div className="kanban-stat-card">
            <span>{label}</span>
            <strong>{value}</strong>
        </div>
    );
}

export default function KanbanPage() {
    const [groups, setGroups] = useState([]);
    const [ownerName, setOwnerName] = useState("");
    const [loadingLeadId, setLoadingLeadId] = useState("");
    const [loading, setLoading] = useState(true);

    const load = async (owner = ownerName) => {
        try {
            setLoading(true);
            const res = await getLeadKanban(owner);
            setGroups(orderGroups(Array.isArray(res) ? res : []));
        } catch (error) {
            console.error(error);
            toast.error("Failed to load lead board.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const totalLeads = useMemo(() => {
        return groups.reduce((sum, group) => sum + (group.leads?.length || 0), 0);
    }, [groups]);

    const statusCounts = useMemo(() => {
        return STATUS_ORDER.reduce((acc, status) => {
            const group = groups.find((item) => item.status === status);
            acc[status] = group?.leads?.length || 0;
            return acc;
        }, {});
    }, [groups]);

    const handleApply = () => {
        load(ownerName);
    };

    const handleClear = () => {
        setOwnerName("");
        load("");
    };

    const handleMove = async (leadId, currentStatus) => {
        const status = normalizeStatus(currentStatus);
        const nextStatus = nextStatusMap[status] || "Contacted";

        try {
            setLoadingLeadId(leadId);
            await updateLeadStatus(leadId, nextStatus);
            toast.success(`Lead moved to ${nextStatus}.`);
            await load(ownerName);
        } catch (error) {
            console.error(error);
            toast.error("Failed to move lead.");
        } finally {
            setLoadingLeadId("");
        }
    };

    return (
        <PageShell title="Lead Board">
            <div className="kanban-page-shell kanban-page-compact">
                <section className="client-settings-hero kanban-hero-compact">
                    <div>
                        <span className="project-kicker">Lead Pipeline</span>
                        <h1>Lead Board</h1>
                        <p>
                            Move website enquiries through follow-up stages. Columns scroll internally,
                            so the board stays usable even with many leads.
                        </p>

                        <div className="client-settings-summary">
                            <span>{totalLeads} leads</span>
                            <span>{groups.length || STATUS_ORDER.length} stages</span>
                            <span>{ownerName ? `Filtered: ${ownerName}` : "All owners"}</span>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="settings-save-btn"
                        onClick={() => load(ownerName)}
                        disabled={loading}
                    >
                        {loading ? "Refreshing..." : "Refresh Board"}
                    </button>
                </section>

                <section className="kanban-stats-grid">
                    <KanbanStat label="New" value={statusCounts.New || 0} />
                    <KanbanStat label="Contacted" value={statusCounts.Contacted || 0} />
                    <KanbanStat label="Won" value={statusCounts.Won || 0} />
                    <KanbanStat label="Lost" value={statusCounts.Lost || 0} />
                </section>

                <section className="lead-filter-card kanban-filter-card">
                    <div className="lead-filter-grid kanban-filter-grid">
                        <label>
                            <span>Pipeline Owner</span>
                            <input
                                placeholder="Filter by owner name..."
                                value={ownerName}
                                onChange={(event) => setOwnerName(event.target.value)}
                            />
                        </label>

                        <div className="lead-filter-actions">
                            <button
                                type="button"
                                className="crm-btn crm-btn-primary"
                                onClick={handleApply}
                                disabled={loading}
                            >
                                Apply Filter
                            </button>

                            <button
                                type="button"
                                className="crm-btn"
                                onClick={handleClear}
                                disabled={loading || !ownerName}
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </section>

                {loading && groups.length === 0 ? (
                    <div className="project-loading-card">Loading lead board...</div>
                ) : groups.length === 0 ? (
                    <section className="project-panel">
                        <div className="project-empty">
                            No leads found. New website enquiries will appear here after visitors submit forms.
                        </div>
                    </section>
                ) : (
                    <section className="kanban-board premium kanban-board-fit">
                        {groups.map((group) => (
                            <div className="kanban-column premium kanban-column-fit" key={group.status}>
                                <div className="kanban-column-head">
                                    <div>
                                        <h3>{group.status}</h3>
                                        <p>{getStatusHelp(group.status)}</p>
                                    </div>

                                    <span>{group.leads?.length || 0}</span>
                                </div>

                                <div className="kanban-cards kanban-cards-scroll">
                                    {(group.leads || []).length === 0 ? (
                                        <div className="kanban-empty-card">
                                            No leads in this stage.
                                        </div>
                                    ) : (
                                        group.leads.map((lead) => {
                                            const currentStatus = normalizeStatus(lead.status || group.status);
                                            const nextStatus = nextStatusMap[currentStatus] || "Contacted";

                                            return (
                                                <article className="kanban-card premium kanban-card-compact" key={lead.id}>
                                                    <div className="kanban-card-title">{getLeadName(lead)}</div>

                                                    <div className="kanban-card-meta">
                                                        <strong>Interested:</strong> {getLeadInterest(lead)}
                                                    </div>

                                                    <div className="kanban-card-meta">{lead.email || "-"}</div>
                                                    <div className="kanban-card-meta">{lead.phone || "-"}</div>

                                                    <div className="kanban-card-meta">
                                                        <strong>Owner:</strong> {lead.ownerName || "Unassigned"}
                                                    </div>

                                                    <div className="kanban-card-meta">
                                                        <strong>Source:</strong> {lead.source || "Website"}
                                                    </div>

                                                    {lead.pageKey || lead.moduleKey ? (
                                                        <div className="kanban-card-meta">
                                                            <strong>Context:</strong> {[lead.pageKey, lead.moduleKey].filter(Boolean).join(" / ")}
                                                        </div>
                                                    ) : null}

                                                    {lead.ctaLabel ? (
                                                        <div className="kanban-card-meta">
                                                            <strong>CTA:</strong> {lead.ctaLabel}
                                                        </div>
                                                    ) : null}

                                                    {lead.notes ? (
                                                        <div className="kanban-card-notes">{lead.notes}</div>
                                                    ) : null}

                                                    <div className="kanban-card-date">
                                                        {formatDate(lead.createdAtUtc)}
                                                    </div>

                                                    <button
                                                        type="button"
                                                        className="crm-btn crm-btn-primary"
                                                        disabled={loadingLeadId === lead.id}
                                                        onClick={() => handleMove(lead.id, currentStatus)}
                                                    >
                                                        {loadingLeadId === lead.id
                                                            ? "Moving..."
                                                            : `Move to ${nextStatus}`}
                                                    </button>
                                                </article>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        ))}
                    </section>
                )}
            </div>
        </PageShell>
    );
}